// ui/HydrationGate.tsx

'use client'

import { useEffect, useState } from 'react'

/**
 * Custom hook that tracks whether the component has been hydrated on the client side.
 * 
 * @returns {boolean} `true` after the component has mounted on the client, `false` during SSR
 * 
 * @example
 * ```tsx
 * function MyComponent() {
 *   const isHydrated = useHydrated()
 *   return isHydrated ? <InteractiveWidget /> : <StaticFallback />
 * }
 * ```
 */
export function useHydrated(): boolean {
  const [hydrated, setHydrated] = useState(false)
  
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- one-shot SSR hydration flag, set once after mount (client-only)
    setHydrated(true)
  }, [])
  
  return hydrated
}

/**
 * A component that conditionally renders content based on hydration status.
 * 
 * During server-side rendering and before hydration, it shows the `fallback` content.
 * After hydration on the client, it switches to the `enhanced` content.
 * 
 * This pattern is useful for:
 * - Progressive enhancement
 * - Avoiding hydration mismatches
 * - Providing SEO-friendly fallbacks for interactive components
 * 
 * @param props - The component props
 * @param props.fallback - Content to show during SSR and before hydration (should be semantic and crawler-friendly)
 * @param props.enhanced - Content to show after hydration (can include interactive elements)
 * 
 * @example
 * ```tsx
 * <HydrationGate
 *   fallback={<div>Loading interactive content...</div>}
 *   enhanced={<InteractiveChart data={chartData} />}
 * />
 * ```
 */
export function HydrationGate({
  fallback, // SSR/shadow HTML (semantic, crawler-friendly)
  enhanced, // interactive island
}: {
  fallback: React.ReactNode
  enhanced: React.ReactNode
}): React.ReactNode {
  const hydrated = useHydrated()
  
  return hydrated ? <>{enhanced}</> : <>{fallback}</>
}
